import { useEffect } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FormSection, FieldInput, FieldTextarea, FieldSelect, FieldPhone,
    FieldRepeater, FieldSwitch, FieldTags, FieldCheckbox, FieldImageCrop, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { CONTACTS_CONSTANTS } from "../_MODULES/_API";
import { normalizeUniqueValue } from "../../../../_UI/recordUniqueness";

const CONTACT_TYPES = [
    { value: "PERSONAL", label: "Personal" },
    { value: "WORK", label: "Trabajo / compañero" },
    { value: "FAMILY", label: "Familia" },
    { value: "FRIEND", label: "Amigo / conocido" },
    { value: "CLIENT", label: "Cliente" },
    { value: "LEAD", label: "Prospecto / potencial cliente" },
    { value: "SUPPLIER", label: "Proveedor" },
    { value: "PARTNER", label: "Socio / partner" },
    { value: "PROFESSIONAL", label: "Profesional" },
    { value: "HEALTH", label: "Salud / médico" },
    { value: "EDUCATION", label: "Educación / docente" },
    { value: "SERVICE", label: "Servicio / comercio" },
    { value: "GOVERNMENT", label: "Organismo / trámite" },
    { value: "EMERGENCY", label: "Emergencia" },
    { value: "OTHER", label: "Otro / personalizado" },
];

const CONTACT_METHODS = [
    { value: "PHONE", label: "Llamada" },
    { value: "WHATSAPP", label: "WhatsApp" },
    { value: "EMAIL", label: "Correo" },
    { value: "SMS", label: "SMS" },
    { value: "TELEGRAM", label: "Telegram" },
    { value: "SIGNAL", label: "Signal" },
    { value: "MESSENGER", label: "Messenger" },
    { value: "INSTAGRAM", label: "Instagram" },
    { value: "LINKEDIN", label: "LinkedIn" },
    { value: "DISCORD", label: "Discord" },
    { value: "MEET", label: "Google Meet" },
    { value: "TEAMS", label: "Microsoft Teams" },
    { value: "OTHER", label: "Otro / personalizado" },
];

const EMPTY_PHONE = { label: "Móvil", number: "", extension: "", isPrimary: false, whatsapp: false, telegram: false, signal: false };
const EMPTY_EMAIL = { label: "Personal", email: "", isPrimary: false };
const EMPTY_ADDRESS = { label: "Casa", street: "", number: "", floor: "", city: "", state: "", postalCode: "", country: "Argentina", notes: "" };
const EMPTY_SOCIAL = { platform: "", username: "", url: "" };
const EMPTY_DATE = { label: "", date: "" };
const EMPTY_CUSTOM = { label: "", value: "" };

const EMPTY_VALUES = {
    groupId: "", isFavorite: false,
    prefix: "", firstName: "", middleName: "", lastName: "", suffix: "", nickname: "",
    contactType: "PERSONAL", relationship: "", organization: "", jobTitle: "", department: "",
    phones: [{ ...EMPTY_PHONE, isPrimary: true, whatsapp: true }], emails: [], addresses: [],
    website: "", socials: [], birthday: "", anniversary: "", importantDates: [],
    documentType: "", documentNumber: "", taxId: "", preferredContactMethod: "PHONE",
    language: "", timezone: "", photoDataUrl: "", photoUrl: "", howWeMet: "", source: "", tags: [],
    notes: "", privateNotes: "", customFields: [],
};

const cloneRows = (rows = []) => rows.map((row) => ({ ...row }));

const toContactPayload = (values) => ({
    prefix: values.prefix, firstName: values.firstName, middleName: values.middleName,
    lastName: values.lastName, suffix: values.suffix, nickname: values.nickname,
    contactType: values.contactType, relationship: values.relationship,
    organization: values.organization, jobTitle: values.jobTitle, department: values.department,
    phones: values.phones, emails: values.emails, addresses: values.addresses,
    website: values.website, socials: values.socials, birthday: values.birthday,
    anniversary: values.anniversary, importantDates: values.importantDates,
    documentType: values.documentType, documentNumber: values.documentNumber, taxId: values.taxId,
    preferredContactMethod: values.preferredContactMethod, language: values.language,
    timezone: values.timezone, photoDataUrl: values.photoDataUrl, photoUrl: values.photoUrl || "", howWeMet: values.howWeMet,
    source: values.source, tags: values.tags, notes: values.notes,
    privateNotes: values.privateNotes, customFields: values.customFields,
});

export const ContactFormModal = ({ isOpen, contact = null, groups = [], contacts = [], defaultGroupId = "", onClose, onSaved }) => {
    const IS_EDIT = !!contact?.id;
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 250,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: {},
        onSubmit: async (values) => {
            const hasIdentity = [values.firstName, values.middleName, values.lastName, values.nickname, values.organization]
                .some((value) => String(value || "").trim());
            if (!hasIdentity) {
                FORM.setError("firstName", "Indicá un nombre, apellido, apodo u organización", { focus: true });
                return;
            }

            const identityName = [values.firstName, values.middleName, values.lastName].map((value) => String(value || "").trim()).filter(Boolean).join(" ")
                || String(values.nickname || "").trim() || String(values.organization || "").trim();
            const phones = new Set((values.phones || []).map((row) => normalizeUniqueValue(String(row?.number || "").replace(/\D/g, ""))).filter(Boolean));
            const emails = new Set((values.emails || []).map((row) => normalizeUniqueValue(row?.email)).filter(Boolean));
            const duplicate = (Array.isArray(contacts) ? contacts : []).find((row) => {
                if (contact?.id && String(row?.id) === String(contact.id)) return false;
                const sameGroup = String(row?.groupId || "") === String(values.groupId || "");
                const sameName = sameGroup && normalizeUniqueValue(row?.displayName) === normalizeUniqueValue(identityName);
                const samePhone = (row?.phones || []).some((phone) => phones.has(normalizeUniqueValue(String(phone?.number || "").replace(/\D/g, ""))));
                const sameEmail = (row?.emails || []).some((email) => emails.has(normalizeUniqueValue(email?.email)));
                return sameName || samePhone || sameEmail;
            });
            if (duplicate) {
                FORM.setError("firstName", `Ya existe un contacto coincidente: ${duplicate.displayName || "contacto existente"}`, { focus: true });
                return;
            }

            let photoDataUrl = values.photoDataUrl || "";
            if (typeof File !== "undefined" && values.photoDataUrl instanceof File) {
                photoDataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || ""));
                    reader.onerror = () => reject(new Error("No se pudo leer la foto"));
                    reader.readAsDataURL(values.photoDataUrl);
                });
            }
            const contactPayload = toContactPayload({ ...values, photoDataUrl });

            const response = IS_EDIT
                ? await actionMutate({
                    method: "PATCH", endpoint: CONTACTS_CONSTANTS.UPDATE, id: contact.id,
                    data: { contact: contactPayload, groupId: values.groupId || null, isFavorite: !!values.isFavorite },
                    successMessage: "Contacto actualizado",
                })
                : await actionMutate({
                    method: "POST", endpoint: CONTACTS_CONSTANTS.CREATE,
                    data: { contact: contactPayload, groupId: values.groupId || null, isFavorite: !!values.isFavorite },
                    successMessage: "Contacto guardado",
                });

            if (!response.success) { FORM.applyApiError(response); return; }
            onSaved?.(response.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        if (!IS_EDIT) {
            FORM.reset({ ...EMPTY_VALUES, groupId: defaultGroupId || "", phones: cloneRows(EMPTY_VALUES.phones) });
            return;
        }
        FORM.reset({
            ...EMPTY_VALUES,
            ...contact,
            groupId: contact.groupId || "",
            isFavorite: !!contact.isFavorite,
            phones: cloneRows(contact.phones),
            emails: cloneRows(contact.emails),
            addresses: cloneRows(contact.addresses),
            socials: cloneRows(contact.socials),
            importantDates: cloneRows(contact.importantDates),
            customFields: cloneRows(contact.customFields),
            tags: Array.isArray(contact.tags) ? [...contact.tags] : [],
        });
    }, [isOpen, contact, defaultGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

    const groupOptions = [
        { value: "", label: "Sin grupo" },
        ...groups.map((item) => ({ value: item.id, label: item.name })),
    ];

    return (
        <AppModal isOpen={isOpen} onClose={onClose} isDirty={FORM.isDirty} size="xl"
            title={IS_EDIT ? "Editar contacto" : "Nuevo contacto"}
            description="Agenda privada: los datos personales se cifran antes de almacenarse en la base de datos."
            footer={<>
                <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>{IS_EDIT ? "Guardar cambios" : "Guardar contacto"}</AppButton>
            </>}>

            <FormSection title="Organización de agenda" columns={2}>
                <FieldSelect {...FORM.field("groupId")} label="Grupo" options={groupOptions} span={2} />
            </FormSection>

            <div className="LStudio__contacts_favorite_toggle">
                <FieldSwitch id="contact_favorite" label="Destacado" text="Mostrar este contacto primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormSection title="Identidad" description="Los datos básicos del contacto." columns={2}>
                <FieldInput {...FORM.field("firstName")} label="Nombre" placeholder="Ej: Juan" />
                <FieldInput {...FORM.field("lastName")} label="Apellido" placeholder="Ej: Pérez" />
                <FieldInput {...FORM.field("middleName")} label="Segundo nombre" />
                <FieldInput {...FORM.field("nickname")} label="Apodo / cómo lo conocés" />
                <FieldInput {...FORM.field("prefix")} label="Prefijo" placeholder="Dr., Ing., Lic." />
                <FieldInput {...FORM.field("suffix")} label="Sufijo" placeholder="Jr., III, etc." />
                <FieldSelect {...FORM.field("contactType")} label="Tipo de contacto" options={CONTACT_TYPES} />
                <FieldInput {...FORM.field("relationship")} label="Relación" placeholder="Amigo, supervisor, familiar..." />
                <FieldImageCrop id="contact_photo" label="Foto del contacto" value={FORM.values.photoDataUrl || FORM.values.photoUrl || null}
                    onChange={(value) => { FORM.setValue("photoDataUrl", value || ""); FORM.setValue("photoUrl", ""); }} aspect={1} aspectOptions={false}
                    cropShape="round" maxWidth={640} quality={0.88} maxSize={5242880} span={2}
                    hint="Elegí una foto desde tu computadora. Se recorta y se guarda dentro del bloque cifrado del contacto." />
            </FormSection>

            <FormSection title="Trabajo y organización" columns={2}>
                <FieldInput {...FORM.field("organization")} label="Empresa / organización" />
                <FieldInput {...FORM.field("jobTitle")} label="Cargo / profesión" />
                <FieldInput {...FORM.field("department")} label="Área / departamento" />
                <FieldInput {...FORM.field("source")} label="Origen del contacto" placeholder="Trabajo, evento, recomendación..." />
            </FormSection>

            <FormSection title="Teléfonos" description="Podés guardar varios números y marcar qué servicios usa cada uno." columns={2}>
                <FieldRepeater id="phones" label="Números" rows={FORM.values.phones} span={2}
                    onChange={(rows) => FORM.setValue("phones", rows)} emptyRow={EMPTY_PHONE} addText="Agregar teléfono" maxRows={12}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`phone_label_${index}`} label="Etiqueta" value={row.label || ""}
                            onChange={(event) => onSet("label", event.target.value)} />
                        <FieldPhone id={`phone_number_${index}`} label="Número" value={row.number || ""}
                            onChange={(value) => onSet("number", value)} country="ar" />
                        <FieldInput id={`phone_extension_${index}`} label="Interno / extensión" value={row.extension || ""}
                            onChange={(event) => onSet("extension", event.target.value)} />
                        <div className="LStudio__contacts_repeat_checks">
                            <FieldCheckbox id={`phone_primary_${index}`} label={null} text="Principal" isChecked={!!row.isPrimary}
                                onChange={(event) => onSet("isPrimary", event.target.checked)} />
                            <FieldCheckbox id={`phone_whatsapp_${index}`} label={null} text="WhatsApp" isChecked={!!row.whatsapp}
                                onChange={(event) => onSet("whatsapp", event.target.checked)} />
                            <FieldCheckbox id={`phone_telegram_${index}`} label={null} text="Telegram" isChecked={!!row.telegram}
                                onChange={(event) => onSet("telegram", event.target.checked)} />
                            <FieldCheckbox id={`phone_signal_${index}`} label={null} text="Signal" isChecked={!!row.signal}
                                onChange={(event) => onSet("signal", event.target.checked)} />
                        </div>
                    </FormGrid>} />
            </FormSection>

            <FormSection title="Correos electrónicos" columns={2}>
                <FieldRepeater id="emails" label="Correos" rows={FORM.values.emails} span={2}
                    onChange={(rows) => FORM.setValue("emails", rows)} emptyRow={EMPTY_EMAIL} addText="Agregar correo" maxRows={12}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`email_label_${index}`} label="Etiqueta" value={row.label || ""}
                            onChange={(event) => onSet("label", event.target.value)} />
                        <FieldInput id={`email_value_${index}`} type="email" label="Correo" value={row.email || ""}
                            onChange={(event) => onSet("email", event.target.value)} />
                        <FieldCheckbox id={`email_primary_${index}`} label={null} text="Correo principal" isChecked={!!row.isPrimary}
                            onChange={(event) => onSet("isPrimary", event.target.checked)} span={2} />
                    </FormGrid>} />
            </FormSection>

            <FormSection title="Direcciones" columns={2}>
                <FieldRepeater id="addresses" label="Domicilios" rows={FORM.values.addresses} span={2}
                    onChange={(rows) => FORM.setValue("addresses", rows)} emptyRow={EMPTY_ADDRESS} addText="Agregar dirección" maxRows={8}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`address_label_${index}`} label="Etiqueta" value={row.label || ""} onChange={(e) => onSet("label", e.target.value)} />
                        <FieldInput id={`address_country_${index}`} label="País" value={row.country || ""} onChange={(e) => onSet("country", e.target.value)} />
                        <FieldInput id={`address_street_${index}`} label="Calle" value={row.street || ""} onChange={(e) => onSet("street", e.target.value)} />
                        <FieldInput id={`address_number_${index}`} label="Número" value={row.number || ""} onChange={(e) => onSet("number", e.target.value)} />
                        <FieldInput id={`address_floor_${index}`} label="Piso / Depto" value={row.floor || ""} onChange={(e) => onSet("floor", e.target.value)} />
                        <FieldInput id={`address_city_${index}`} label="Ciudad / localidad" value={row.city || ""} onChange={(e) => onSet("city", e.target.value)} />
                        <FieldInput id={`address_state_${index}`} label="Provincia / estado" value={row.state || ""} onChange={(e) => onSet("state", e.target.value)} />
                        <FieldInput id={`address_postal_${index}`} label="Código postal" value={row.postalCode || ""} onChange={(e) => onSet("postalCode", e.target.value)} />
                        <FieldTextarea id={`address_notes_${index}`} label="Referencia / notas" value={row.notes || ""}
                            onChange={(e) => onSet("notes", e.target.value)} rows={2} span={2} />
                    </FormGrid>} />
            </FormSection>

            <FormSection title="Web y redes" columns={2}>
                <FieldInput {...FORM.field("website")} label="Sitio web" placeholder="https://..." span={2} />
                <FieldRepeater id="socials" label="Perfiles sociales" rows={FORM.values.socials} span={2}
                    onChange={(rows) => FORM.setValue("socials", rows)} emptyRow={EMPTY_SOCIAL} addText="Agregar red" maxRows={16}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`social_platform_${index}`} label="Plataforma" value={row.platform || ""} placeholder="LinkedIn, Instagram..." onChange={(e) => onSet("platform", e.target.value)} />
                        <FieldInput id={`social_user_${index}`} label="Usuario" value={row.username || ""} onChange={(e) => onSet("username", e.target.value)} />
                        <FieldInput id={`social_url_${index}`} label="URL" value={row.url || ""} onChange={(e) => onSet("url", e.target.value)} span={2} />
                    </FormGrid>} />
            </FormSection>

            <FormSection title="Identificación y fechas" description="Todos estos valores también se almacenan dentro del bloque cifrado." columns={2}>
                <FieldInput {...FORM.field("birthday")} type="date" label="Cumpleaños" />
                <FieldInput {...FORM.field("anniversary")} type="date" label="Aniversario" />
                <FieldInput {...FORM.field("documentType")} label="Tipo de documento" placeholder="DNI, pasaporte..." />
                <FieldInput {...FORM.field("documentNumber")} label="Número de documento" />
                <FieldInput {...FORM.field("taxId")} label="CUIT / CUIL / identificación fiscal" span={2} />
                <FieldRepeater id="importantDates" label="Otras fechas importantes" rows={FORM.values.importantDates} span={2}
                    onChange={(rows) => FORM.setValue("importantDates", rows)} emptyRow={EMPTY_DATE} addText="Agregar fecha" maxRows={16}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`date_label_${index}`} label="Motivo" value={row.label || ""} onChange={(e) => onSet("label", e.target.value)} />
                        <FieldInput id={`date_value_${index}`} type="date" label="Fecha" value={row.date || ""} onChange={(e) => onSet("date", e.target.value)} />
                    </FormGrid>} />
            </FormSection>

            <FormSection title="Preferencias" columns={2}>
                <FieldSelect {...FORM.field("preferredContactMethod")} label="Medio preferido" options={CONTACT_METHODS} />
                <FieldInput {...FORM.field("language")} label="Idioma" placeholder="Español, inglés..." />
                <FieldInput {...FORM.field("timezone")} label="Zona horaria" placeholder="America/Argentina/Buenos_Aires" />
                <FieldInput {...FORM.field("howWeMet")} label="Cómo se conocieron" />
                <FieldTags id="contact_tags" label="Etiquetas" value={FORM.values.tags}
                    onChange={(tags) => FORM.setValue("tags", tags)} maxTags={30} span={2} />
            </FormSection>

            <FormSection title="Notas" columns={2}>
                <FieldTextarea {...FORM.field("notes")} label="Notas generales" rows={5} span={2} />
                <FieldTextarea {...FORM.field("privateNotes")} label="Notas privadas / sensibles" rows={5} span={2}
                    hint="Se cifran junto con el resto del contacto." />
                <FieldRepeater id="customFields" label="Campos personalizados" rows={FORM.values.customFields} span={2}
                    onChange={(rows) => FORM.setValue("customFields", rows)} emptyRow={EMPTY_CUSTOM} addText="Agregar campo" maxRows={30}
                    renderRow={({ row, index, onSet }) => <FormGrid columns={2}>
                        <FieldInput id={`custom_label_${index}`} label="Nombre del campo" value={row.label || ""} onChange={(e) => onSet("label", e.target.value)} />
                        <FieldInput id={`custom_value_${index}`} label="Valor" value={row.value || ""} onChange={(e) => onSet("value", e.target.value)} />
                    </FormGrid>} />
            </FormSection>
        </AppModal>
    );
};
